import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const CreditDetailsModal = ({ isOpen, onClose, credit }) => {
  const [activeTab, setActiveTab] = useState('overview');

  if (!isOpen || !credit) return null;

  const tabs = [
    { id: 'overview', label: 'Overview', icon: 'FileText' },
    { id: 'verification', label: 'Verification', icon: 'Shield' },
    { id: 'methodology', label: 'Methodology', icon: 'BookOpen' },
    { id: 'transfers', label: 'Transfer History', icon: 'ArrowRightLeft' },
    { id: 'ratings', label: 'Third-Party Ratings', icon: 'Star' }
  ];

  const getIntegrityBadgeColor = (status) => {
    switch (status) {
      case 'High': return 'bg-success text-success-foreground';
      case 'Medium': return 'bg-warning text-warning-foreground';
      case 'Low': return 'bg-error text-error-foreground';
      default: return 'bg-muted text-muted-foreground';
    }
  };

  const getCCPStatusColor = (status) => {
    switch (status) {
      case 'Approved': return 'text-success';
      case 'Under Review': return 'text-warning';
      case 'Not Approved': return 'text-error';
      default: return 'text-muted-foreground';
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
      <div className="bg-card border border-border rounded-lg shadow-lg w-full max-w-4xl max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-border">
          <div className="flex items-center space-x-4">
            <div className={`w-3 h-3 rounded-full ${credit?.registry === 'Verra' ? 'bg-blue-500' : 
              credit?.registry === 'Gold Standard' ? 'bg-yellow-500' : 
              credit?.registry === 'ACR' ? 'bg-green-500' : 'bg-gray-500'}`}></div>
            <div>
              <h2 className="text-xl font-semibold text-card-foreground">Credit Details</h2>
              <p className="text-sm text-muted-foreground font-mono">{credit?.serialNumber}</p>
            </div>
          </div>
          <Button variant="ghost" iconName="X" onClick={onClose} />
        </div>

        {/* Tabs */}
        <div className="flex border-b border-border overflow-x-auto">
          {tabs?.map((tab) => (
            <button
              key={tab?.id}
              onClick={() => setActiveTab(tab?.id)}
              className={`flex items-center space-x-2 px-4 py-3 text-sm font-medium whitespace-nowrap transition-smooth ${
                activeTab === tab?.id
                  ? 'border-b-2 border-primary text-primary bg-primary/5' :'text-muted-foreground hover:text-card-foreground hover:bg-muted/50'
              }`}
            >
              <Icon name={tab?.icon} size={16} />
              <span>{tab?.label}</span>
            </button>
          ))}
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[60vh]">
          {activeTab === 'overview' && (
            <div className="space-y-6">
              {/* Key Information */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-4">
                  <div>
                    <h3 className="text-lg font-semibold text-card-foreground mb-3">Basic Information</h3>
                    <div className="space-y-3">
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Registry:</span>
                        <span className="font-medium text-card-foreground">{credit?.registry}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Registry ID:</span>
                        <span className="font-mono text-sm text-card-foreground">{credit?.registryId}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Vintage:</span>
                        <span className="font-medium text-card-foreground">{credit?.vintage}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Quantity:</span>
                        <span className="font-medium text-card-foreground">{credit?.quantity?.toLocaleString()} tCO2e</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Status:</span>
                        <span className="font-medium text-card-foreground">{credit?.status}</span>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="space-y-4">
                  <div>
                    <h3 className="text-lg font-semibold text-card-foreground mb-3">Quality Indicators</h3>
                    <div className="space-y-3">
                      <div className="flex justify-between items-center">
                        <span className="text-muted-foreground">Integrity Level:</span>
                        <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getIntegrityBadgeColor(credit?.integrityLabel)}`}>
                          {credit?.integrityLabel}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">CCP Status:</span>
                        <span className={`font-medium ${getCCPStatusColor(credit?.ccpStatus)}`}>{credit?.ccpStatus}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Article 6 Eligible:</span>
                        <span className="font-medium text-card-foreground">{credit?.article6Eligible ? 'Yes' : 'No'}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Tokenized:</span>
                        <span className="font-medium text-card-foreground">{credit?.tokenized ? 'Yes' : 'No'}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Project Information */}
              <div>
                <h3 className="text-lg font-semibold text-card-foreground mb-3">Project Information</h3>
                <div className="bg-muted/30 rounded-lg p-4 space-y-3">
                  <div>
                    <span className="text-sm text-muted-foreground">Project Name:</span>
                    <div className="font-medium text-card-foreground">{credit?.projectName}</div>
                  </div>
                  <div>
                    <span className="text-sm text-muted-foreground">Location:</span>
                    <div className="font-medium text-card-foreground">{credit?.projectLocation}</div>
                  </div>
                  <div>
                    <span className="text-sm text-muted-foreground">Project Type:</span>
                    <div className="font-medium text-card-foreground">{credit?.projectType}</div>
                  </div>
                  <div>
                    <span className="text-sm text-muted-foreground">Description:</span>
                    <div className="text-card-foreground">{credit?.projectDescription}</div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'verification' && (
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-semibold text-card-foreground mb-4">Verification Details</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-3">
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Verification Body:</span>
                      <span className="font-medium text-card-foreground">{credit?.verificationBody}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Last Verification:</span>
                      <span className="font-medium text-card-foreground">{credit?.lastVerification}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Monitoring Period:</span>
                      <span className="font-medium text-card-foreground">{credit?.monitoringPeriod}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Verification Standard:</span>
                      <span className="font-medium text-card-foreground">{credit?.verificationStandard}</span>
                    </div>
                  </div>
                  <div className="space-y-3">
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Issuance Date:</span>
                      <span className="font-medium text-card-foreground">{credit?.issuanceDate}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Verification Report:</span>
                      <Button variant="link" size="sm" iconName="ExternalLink">
                        View Report
                      </Button>
                    </div>
                  </div>
                </div>
              </div>

              <div>
                <h4 className="font-medium text-card-foreground mb-3">MRV Data Sources</h4>
                <div className="space-y-2">
                  {credit?.mrvSources?.map((source, index) => (
                    <div key={index} className="flex items-center justify-between p-3 bg-muted/30 rounded-lg">
                      <div className="flex items-center space-x-3">
                        <Icon name="Database" size={16} className="text-muted-foreground" />
                        <div>
                          <div className="font-medium text-card-foreground">{source?.provider}</div>
                          <div className="text-xs text-muted-foreground">{source?.type}</div>
                        </div>
                      </div>
                      <div className="text-sm text-muted-foreground">{source?.lastUpdate}</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {activeTab === 'methodology' && (
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-semibold text-card-foreground mb-4">Methodology Information</h3>
                <div className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Methodology:</span>
                      <span className="font-medium text-card-foreground">{credit?.methodology}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Version:</span>
                      <span className="font-medium text-card-foreground">{credit?.methodologyVersion}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Category:</span>
                      <span className="font-medium text-card-foreground">{credit?.methodologyCategory}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Approval Date:</span>
                      <span className="font-medium text-card-foreground">{credit?.methodologyApprovalDate}</span>
                    </div>
                  </div>
                  
                  <div>
                    <span className="text-sm text-muted-foreground">Description:</span>
                    <div className="mt-2 p-3 bg-muted/30 rounded-lg text-card-foreground">
                      {credit?.methodologyDescription}
                    </div>
                  </div>
                </div>
              </div>

              <div>
                <h4 className="font-medium text-card-foreground mb-3">Additionality Assessment</h4>
                <div className="space-y-3">
                  <div className="flex items-center space-x-3">
                    <Icon name="CheckCircle" size={16} className="text-success" />
                    <span className="text-card-foreground">Financial additionality demonstrated</span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <Icon name="CheckCircle" size={16} className="text-success" />
                    <span className="text-card-foreground">Regulatory additionality confirmed</span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <Icon name="CheckCircle" size={16} className="text-success" />
                    <span className="text-card-foreground">Common practice analysis completed</span>
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'transfers' && (
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-card-foreground">Transfer History</h3>
              <div className="space-y-3">
                {credit?.transferHistory?.map((transfer, index) => (
                  <div key={index} className="flex items-center justify-between p-4 border border-border rounded-lg">
                    <div className="flex items-center space-x-4">
                      <div className="flex items-center justify-center w-8 h-8 bg-muted rounded-full">
                        <Icon name="ArrowRightLeft" size={16} className="text-muted-foreground" />
                      </div>
                      <div>
                        <div className="font-medium text-card-foreground">{transfer?.type}</div>
                        <div className="text-sm text-muted-foreground">
                          {transfer?.from} → {transfer?.to}
                        </div>
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="text-sm font-medium text-card-foreground">{transfer?.date}</div>
                      <div className="text-xs text-muted-foreground">{transfer?.quantity} tCO2e</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {activeTab === 'ratings' && (
            <div className="space-y-6">
              <h3 className="text-lg font-semibold text-card-foreground">Third-Party Ratings</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {credit?.thirdPartyRatings?.map((rating, index) => (
                  <div key={index} className="p-4 border border-border rounded-lg">
                    <div className="flex items-center justify-between mb-3">
                      <h4 className="font-medium text-card-foreground">{rating?.provider}</h4>
                      <div className="flex items-center space-x-2">
                        <span className="text-sm font-medium text-card-foreground">{rating?.score}</span>
                        <div className="flex">
                          {[...Array(5)]?.map((_, i) => (
                            <Icon
                              key={i}
                              name="Star"
                              size={16}
                              className={i < rating?.stars ? 'text-warning fill-current' : 'text-muted-foreground'}
                            />
                          ))}
                        </div>
                      </div>
                    </div>
                    <p className="text-sm text-muted-foreground">{rating?.assessment}</p>
                    <div className="mt-3 text-xs text-muted-foreground">
                      Last updated: {rating?.lastUpdate}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between p-6 border-t border-border">
          <div className="text-sm text-muted-foreground">
            Last updated: {credit?.lastUpdated}
          </div>
          <div className="flex items-center space-x-3">
            <Button variant="outline" iconName="Download">
              Export Data
            </Button>
            <Button variant="default" iconName="FileText">
              Generate Dossier
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CreditDetailsModal;